import { useState } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { Save } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import useEscapeClose from "@/hooks/useEscapeClose";
// Spec iter132 pt.2 (Feb 2026) — capitalisation auto AVI.
import { withFirstLetterCapitalized } from "@/lib/autoCapitalize";

/**
 * AviQuickEditModal — édition rapide d'une demande AVI depuis le tableau,
 * sans avoir à ouvrir la fiche complète.
 *
 * Champs éditables :
 *   - Objet          (obligatoire, 3-160 caractères)
 *   - Description    (optionnel, jusqu'à 4000 caractères)
 *   - Priorité       (immédiat / urgent / moyen / normal)
 *
 * Règles (§4) :
 *   - Réservé au demandeur ou aux admins.
 *   - Uniquement quand le statut est `en_attente` (rien n'a encore été
 *     décidé — sinon l'édition pourrait invalider les réponses).
 *
 * Les participants ne sont PAS modifiables ici : leur changement peut
 * réinitialiser les décisions, on force donc à passer par la fiche
 * complète pour cette opération plus lourde.
 */
const PRIORITIES = [
  { key: "immediat", label: "Immédiat", color: "#EF4444" },
  { key: "urgent",   label: "Urgent",   color: "#F97316" },
  { key: "moyen",    label: "Moyen",    color: "#F9BC26" },
  { key: "normal",   label: "Normal",   color: "#4ADE80" },
];

export default function AviQuickEditModal({ avi, onClose, onSaved }) {
  const meta = AVI_SUBTYPES[avi.avi_type];
  const [subject, setSubject] = useState(avi.subject || "");
  const [description, setDescription] = useState(avi.description || "");
  const [priority, setPriority] = useState(avi.priority || "normal");
  const [saving, setSaving] = useState(false);

  const canSave = !saving && subject.trim().length >= 3 && (
    subject !== avi.subject ||
    (description || "") !== (avi.description || "") ||
    priority !== avi.priority
  );

  // Spec rev171 (2026-02) — Échap ferme la modale via le hook partagé
  // (pile LIFO : ne ferme que celle du dessus si plusieurs modales
  // sont empilées).
  useEscapeClose(onClose);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const patch = {};
      if (subject !== avi.subject) patch.subject = subject.trim();
      if ((description || "") !== (avi.description || "")) patch.description = description || null;
      if (priority !== avi.priority) patch.priority = priority;
      await api.patch(`/avi/${avi.id}`, patch);
      toast.success("Demande modifiée");
      onSaved?.();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="avi-quick-edit-modal"
    >
      <div
        className="rounded-2xl w-full max-w-xl overflow-hidden"
        style={{ background: "#161616", border: `2px solid ${meta.color}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4"
             style={{ borderBottom: "1px solid #2A2A2A" }}>
          <div className="flex items-center gap-2.5">
            <meta.Icon size={20} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
                Édition rapide · {meta.label}
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">{avi.reference}</div>
            </div>
          </div>
          <DeskCloseX onClick={onClose} testId="avi-quick-edit-close" />
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Objet *
            </label>
            <input
              type="text"
              value={subject}
              onChange={withFirstLetterCapitalized((e) => setSubject(e.target.value))}
              maxLength={160}
              className="w-full h-10 px-3 rounded-md text-sm placeholder:text-[#666]"
              style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED" }}
              data-testid="avi-quick-edit-subject"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Description
            </label>
            <textarea
              value={description}
              onChange={withFirstLetterCapitalized((e) => setDescription(e.target.value))}
              rows={4}
              maxLength={4000}
              className="w-full p-3 rounded-md text-sm placeholder:text-[#666] resize-none"
              style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED", minHeight: 90 }}
              data-testid="avi-quick-edit-description"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">
              Priorité
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {PRIORITIES.map((p) => {
                const active = priority === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPriority(p.key)}
                    className="h-10 rounded-md text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-all"
                    style={{
                      background: active ? `${p.color}22` : "transparent",
                      border: `1.5px solid ${active ? p.color : "#383838"}`,
                      color: active ? p.color : "#9CA3AF",
                    }}
                    data-testid={`avi-quick-edit-priority-${p.key}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="text-[10.5px] text-muted-foreground italic pt-1">
            Les participants ne sont pas modifiables ici — utilisez la fiche complète pour les ajuster.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="h-9 px-4 rounded-lg text-[12px] font-semibold text-muted-foreground hover:text-white"
                    data-testid="avi-quick-edit-cancel">
              Annuler
            </button>
            <button type="button" onClick={submit} disabled={!canSave}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[12px] font-semibold text-white transition-all"
                    style={{
                      background: canSave ? meta.color : "#2A2A2A",
                      boxShadow: canSave ? `0 4px 14px ${meta.color}55` : "none",
                      cursor: canSave ? "pointer" : "not-allowed",
                    }}
                    data-testid="avi-quick-edit-save">
              <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
